/**
 * PolicyPanel — the rules that decide act / ask first / never for every write (SPEC §8), and the
 * NEVER list no rule can unlock. Site admins (canEdit) can enable or disable a rule; everyone else
 * sees the list read-only.
 */

import { X, ShieldCheck, ShieldAlert, Hand, Ban } from 'lucide-react';
import { ruleText } from './agentUi.js';
import './PolicyPanel.css';

const OUTCOME = {
  act: { Icon: ShieldCheck, label: 'Runs freely', tone: 'good' },
  ask: { Icon: Hand, label: 'Ask first', tone: 'decide' },
  never: { Icon: Ban, label: 'Never', tone: 'bad' },
};

export default function PolicyPanel({
  rules = [], never = [], canEdit = false, onToggle, onClose, loading = false, error = null, busyId = null,
}) {
  const ordered = [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0) || String(a.id).localeCompare(String(b.id)));
  return (
    <section className="agent-panel pol" aria-label="Rules">
      <header className="agent-panel__head">
        <div className="agent-panel__titles">
          <span className="agent-panel__kicker">Rules</span>
          <h2 className="agent-panel__title">What the specialists may do</h2>
        </div>
        {onClose && <button type="button" className="agent-icon" onClick={onClose} aria-label="Close rules"><X size={14} aria-hidden="true" /></button>}
      </header>
      <div className="agent-panel__body">
        {!canEdit && <p className="pol__note">Read only. A site admin can enable or disable rules.</p>}
        {loading && <p className="pol__note">Loading rules…</p>}
        {error && <p className="pol__error" role="alert">{error}</p>}

        <table className="pol__table">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Tool</th>
              <th>Outcome</th>
              <th className="pol__num">Priority</th>
              <th>On</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((r) => {
              const o = OUTCOME[r.outcome] ?? OUTCOME.ask;
              const OutcomeIcon = o.Icon;
              return (
                <tr key={r.id} className={r.enabled ? undefined : 'is-off'}>
                  <td>
                    <div className="pol__desc">{ruleText(r.description) || r.id}</div>
                    <div className="pol__id">{r.id}</div>
                  </td>
                  <td className="pol__tool">{r.tool}</td>
                  <td>
                    <span className={`pol__outcome is-${o.tone}`}><OutcomeIcon size={11} aria-hidden="true" /> {o.label}</span>
                  </td>
                  <td className="pol__num">{r.priority}</td>
                  <td>
                    {canEdit ? (
                      <label className="pol__switch">
                        <input
                          type="checkbox"
                          checked={Boolean(r.enabled)}
                          disabled={busyId === r.id}
                          onChange={(e) => onToggle?.(r.id, e.target.checked)}
                          aria-label={`${r.enabled ? 'Disable' : 'Enable'}: ${ruleText(r.description) || r.id}`}
                        />
                        <span>{r.enabled ? 'On' : 'Off'}</span>
                      </label>
                    ) : (
                      <span className="pol__state">{r.enabled ? 'On' : 'Off'}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && ordered.length === 0 && (
              <tr><td colSpan={5} className="pol__note">No rules loaded.</td></tr>
            )}
          </tbody>
        </table>

        <h3 className="pol__never-title"><ShieldAlert size={13} aria-hidden="true" /> Never, whatever the rules say</h3>
        <ul className="pol__never">
          {never.map((n) => (
            <li key={n.id}><span className="pol__id">{n.id}</span> {n.description}</li>
          ))}
          {never.length === 0 && <li className="pol__note">No NEVER list loaded.</li>}
        </ul>
      </div>
    </section>
  );
}
